/*
 * Created by Anup Shrestha (anup.stha012@gmail.com)
 * Copyright (c) 2022.  All rights reserved.
 * Last modified 5/9/22, 3:00 PM
 *
 */

import React, { Fragment, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { CaretDown } from 'phosphor-react';
import { useAppSelector } from 'stores';

import NavItem from '../NavItem';
import { RouteObjectType } from '../sidebarRoutes';

export type NavDropdownPropType = {
  subRoutes: RouteObjectType;
};

const NavDropdown: React.FC<NavDropdownPropType> = ({ subRoutes }) => {
  const { pathname } = useLocation();
  const isPathSelected = subRoutes.children?.some(
    (route) => route.link && pathname.includes(route.link)
  );
  const [expand, setExpand] = useState(isPathSelected);

  const open = useAppSelector((state) => state.sidebar.open);
  const mainItemStyles =
    'text-gray-500 hover:text-gray-900 py-2.5 rounded-lg cursor-pointer hover:bg-white relative';

  const onExpandChange = () => {
    setExpand((expand) => !expand);
  };

  return (
    <ul className="flex w-full flex-col">
      <li className={mainItemStyles} onClick={() => onExpandChange()}>
        <div className="peer flex w-full">
          <div
            className={`relative flex w-full items-start justify-start px-4 ${
              open ? 'translate-x-0' : 'w-0 -translate-x-96'
            } w-full transition-transform duration-100`}
          >
            <span
              className={`flex w-full items-center gap-x-4 text-lg ${
                expand ? 'font-semibold text-gray-900' : 'font-medium'
              } `}
            >
              <div>{subRoutes.icon}</div>

              <div className="flex w-full items-center justify-between">
                <span className="sm:text-lg">{subRoutes.title}</span>
                <CaretDown weight="bold" size={24} />
              </div>
            </span>
          </div>

          <div
            className={`relative flex items-center  ${
              !open
                ? '-translate-x-8 px-4 opacity-100 transition-opacity duration-500 sm:hidden'
                : 'w-0 p-0 opacity-0'
            }  `}
          >
            <div className="sm:hidden">{subRoutes.icon}</div>
          </div>
        </div>

        {!open && (
          <div className="pointer-events-none absolute top-0 left-[150%] z-0 flex h-10 w-0 translate-y-1/4 items-center justify-center rounded-md bg-gray-800 text-lg text-gray-50 opacity-0 shadow-E200 transition-all transition-all duration-300 peer-hover:w-36 peer-hover:opacity-100">
            {subRoutes.title}
          </div>
        )}
      </li>

      {expand && (
        <div className={open ? 'ml-8 space-y-1 border-l-2 border-gray-300' : ''}>
          {expand &&
            subRoutes.children &&
            subRoutes.children.map((route) => {
              if (route.link === false) {
                if (route.modal) {
                  const Modal = route.modal;

                  return (
                    <Fragment key={`${route.id}-${route.title}`}>
                      <Modal>
                        <NavItem
                          route={route}
                          key={`${route.id}-${route.title}`}
                          containerClassName={open ? `pl-4` : ''}
                        />
                      </Modal>
                    </Fragment>
                  );
                }
              }

              if (route.children) {
                return <NavDropdown subRoutes={route} key={`${route.id}-${route.title}`} />;
              }

              return (
                <NavItem
                  key={`${route.id}-${route.title}`}
                  route={route}
                  containerClassName={open ? `pl-4` : ''}
                />
              );
            })}
        </div>
      )}
    </ul>
  );
};

export default NavDropdown;
